import { useAppNavigation } from "@/features/Navigation/hooks/useAppNavigation";
import { useTeams } from "@/features/Teams/hooks/useTeams";
import { AbsoluteCenter, Box, Flex, Text } from "@chakra-ui/react";
import { Helmet } from "react-helmet";
import { useParams, useSearchParams } from "react-router-dom";
import { createPromptbookFromTemplate } from "../../actions/createPromptbookFromTemplate";
import { usePromptbookTemplates } from "../../hooks/usePromptbookTemplates";
import { ImportTemplateForm, ImportTemplateFormSkeleton } from "./ImportTemplateForm";
import { ImportTemplatePageHeader, ImportTemplatePageHeaderSkeleton } from "./ImportTemplatePageHeader";
import { ImportTemplateComponentProps } from "./types";

export const ImportTemplatePageSkeleton = () => {
  return (
    <Box>
      <ImportTemplatePageHeaderSkeleton />
      <AbsoluteCenter
        marginTop={["4rem", "4rem", "0"]}
        border="1px solid"
        borderColor="border.secondary"
        borderRadius={"radius-lg"}
      >
        <Flex borderBottom="1px solid" borderColor="border.secondary" padding="spacing-lg">
          <Text textStyle="heading/2">Create Promptbook from Template</Text>
        </Flex>
        <ImportTemplateFormSkeleton />
      </AbsoluteCenter>
    </Box>
  );
};

export const ImportTemplatePageComponent = (props: ImportTemplateComponentProps) => {
  const { templateName, isLoading } = props;
  return (
    <Box>
      <Helmet>
        <title>{templateName || ""} | Import Template | Prompt Studio</title>
        <meta name="description" content="Create a new Promptbook from a template" />
      </Helmet>
      <ImportTemplatePageHeader />
      <AbsoluteCenter
        marginTop={["4rem", "4rem", "0"]}
        border="1px solid"
        borderColor="border.secondary"
        borderRadius={"radius-lg"}
      >
        <Flex borderBottom="1px solid" borderColor="border.secondary" padding="spacing-lg">
          <Text textStyle="heading/2">Create Promptbook from Template</Text>
        </Flex>
        {isLoading ? <ImportTemplateFormSkeleton /> : <ImportTemplateForm {...props} />}
      </AbsoluteCenter>
    </Box>
  );
};

export const ImportTemplatePage = () => {
  const { templateId } = useParams();
  const { navigateToPromptbook } = useAppNavigation();

  const teamsQuery = useTeams();
  const [searchParams] = useSearchParams();
  const teamId = searchParams.get("team") || undefined;
  const templatesQuery = usePromptbookTemplates(teamId);

  const sortedTeams = teamsQuery.data?.sort((a, b) => a.name.localeCompare(b.name));

  const teams = sortedTeams?.map((team) => ({
    teamName: team.name,
    teamId: team.id,
  }));

  const template = templatesQuery.data?.find((template) => template.resource_id === templateId);

  const isLoading = templatesQuery.isLoading || teamsQuery.isLoading;

  return (
    <ImportTemplatePageComponent
      onCreatePromptbook={async (workspaceId, name, selectedTeamId) => {
        if (!templateId) return;
        const res = await createPromptbookFromTemplate(templateId, name, workspaceId, selectedTeamId);
        const newPromptbookId = res?.newPromptbookId;
        if (newPromptbookId) navigateToPromptbook(newPromptbookId);
      }}
      isLoading={isLoading}
      teams={teams}
      templateDescription={template?.description}
      templateImageUrl={template?.thumbnail_url}
      templateName={template?.name}
    />
  );
};
